import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ImageInput } from "@/components/ImageInput";

// Generic brand-scoped CMS editor for Docstate content collections
// (Insights, Gallery, Testimonials, FAQs). Drives /admin/realestate/{endpoint}.
export const ReContentTab = ({ endpoint, title, description, schema, primaryField, secondaryField, testid }) => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | item | null
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    () => api.get(`/admin/realestate/${endpoint}`).then((r) => setItems(r.data)).catch(() => setItems([])),
    [endpoint]
  );
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({}); setEditing("new"); };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item); };
  const close = () => { setEditing(null); setForm({}); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload._id;
      if (editing === "new") await api.post(`/admin/realestate/${endpoint}`, payload);
      else await api.put(`/admin/realestate/${endpoint}/${editing.id}`, payload);
      toast.success("Saved");
      close();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/realestate/${endpoint}/${item.id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  if (items === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  const renderField = (fd) => {
    const val = form[fd.name];
    if (fd.type === "checkbox") {
      return (
        <label key={fd.name} className="flex items-center gap-3 rounded-lg border border-[#E8E2D9] bg-white px-4 py-3">
          <input type="checkbox" checked={val ?? fd.default ?? false} onChange={(e) => setForm({ ...form, [fd.name]: e.target.checked })} data-testid={`${testid}-field-${fd.name}`} className="h-4 w-4 accent-[#B8912E]" />
          <span className="text-sm text-[#1A1615]">{fd.label}</span>
        </label>
      );
    }
    return (
      <div key={fd.name} className={fd.full ? "sm:col-span-2" : ""}>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#5A504B]">{fd.label}</label>
        {fd.type === "textarea" ? (
          <textarea rows={fd.rows || 4} value={val || ""} onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })} data-testid={`${testid}-field-${fd.name}`} className="input-lux resize-none text-sm" />
        ) : fd.type === "image" ? (
          <ImageInput value={val || ""} onChange={(v) => setForm({ ...form, [fd.name]: v })} testid={`${testid}-field-${fd.name}`} />
        ) : (
          <input type={fd.type === "number" ? "number" : "text"} placeholder={fd.placeholder || ""} value={val ?? ""} onChange={(e) => setForm({ ...form, [fd.name]: fd.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} data-testid={`${testid}-field-${fd.name}`} className="input-lux text-sm" />
        )}
      </div>
    );
  };

  return (
    <div data-testid={`${testid}-tab`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">{title}</h2>
          {description && <p className="mt-1 text-xs text-[#91837C]">{description}</p>}
        </div>
        <button onClick={openNew} data-testid={`${testid}-add`} className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-xs">
          <Plus className="h-4 w-4" /> Add New
        </button>
      </div>

      {editing && (
        <div className="glass mt-6 rounded-3xl p-7" data-testid={`${testid}-editor`}>
          <div className="mb-5 flex items-center justify-between">
            <p className="font-serif text-lg font-semibold text-[#1A1615]">{editing === "new" ? "New item" : "Edit item"}</p>
            <button onClick={close} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8E2D9] text-[#5A504B]"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            {schema.map(renderField)}
          </div>
          <div className="mt-6 flex gap-2">
            <button onClick={save} disabled={saving} data-testid={`${testid}-save`} className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-xs disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
            </button>
            <button onClick={close} className="btn-outline-lux px-5 py-3 text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {items.map((item) => (
          <div key={item.id} className="glass flex flex-wrap items-center gap-4 rounded-2xl p-4" data-testid={`${testid}-item-${item.id}`}>
            {item.image || item.cover_image || item.photo ? (
              <img src={item.image || item.cover_image || item.photo} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#1A1615]">{item[primaryField] || "Untitled"}</p>
              {secondaryField && item[secondaryField] && <p className="mt-0.5 line-clamp-2 text-xs font-light text-[#91837C]">{item[secondaryField]}</p>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => openEdit(item)} data-testid={`${testid}-edit-${item.id}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E8E2D9] text-[#5A504B] hover:bg-[#F5EBE6]"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(item)} data-testid={`${testid}-delete-${item.id}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-red-200 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">Nothing here yet — tap "Add New" to create the first one.</p>}
      </div>
    </div>
  );
};

// Preset configs for each content section.
export const RE_CONTENT_PRESETS = {
  insights: {
    endpoint: "posts", title: "Insights", description: "Market insights & blog posts for the Docstate portal.",
    primaryField: "title", secondaryField: "excerpt", testid: "re-insights",
    schema: [
      { name: "title", label: "Title", full: true },
      { name: "slug", label: "Slug (optional)" },
      { name: "cover_image", label: "Cover image", type: "image" },
      { name: "excerpt", label: "Excerpt", type: "textarea", rows: 2, full: true },
      { name: "body", label: "Body", type: "textarea", rows: 10, full: true },
      { name: "tags", label: "Tags (comma-separated)" },
      { name: "published", label: "Published (visible on site)", type: "checkbox", default: true },
    ],
  },
  gallery: {
    endpoint: "gallery", title: "Gallery", description: "Showcase imagery for the Docstate portal.",
    primaryField: "title", secondaryField: "caption", testid: "re-gallery",
    schema: [
      { name: "image", label: "Image", type: "image", full: true },
      { name: "title", label: "Title" },
      { name: "caption", label: "Caption", full: true },
    ],
  },
  testimonials: {
    endpoint: "testimonials", title: "Testimonials", description: "Client reviews that build trust.",
    primaryField: "name", secondaryField: "quote", testid: "re-testimonials",
    schema: [
      { name: "name", label: "Client name" },
      { name: "role", label: "Role / location" },
      { name: "photo", label: "Photo", type: "image" },
      { name: "rating", label: "Rating (1-5)", type: "number" },
      { name: "quote", label: "Quote", type: "textarea", rows: 3, full: true },
    ],
  },
  faqs: {
    endpoint: "faqs", title: "FAQs", description: "Answers to common buyer & tenant questions.",
    primaryField: "question", secondaryField: "answer", testid: "re-faqs",
    schema: [
      { name: "question", label: "Question", full: true },
      { name: "answer", label: "Answer", type: "textarea", rows: 4, full: true },
      { name: "category", label: "Category (optional)" },
    ],
  },
  guides: {
    endpoint: "guides", title: "Area Guides", description: "Community guides that help buyers choose where to live.",
    primaryField: "title", secondaryField: "excerpt", testid: "re-guides",
    schema: [
      { name: "title", label: "Title / community", full: true },
      { name: "community", label: "Community name" },
      { name: "slug", label: "Slug (optional)" },
      { name: "cover_image", label: "Cover image", type: "image" },
      { name: "excerpt", label: "Excerpt", type: "textarea", rows: 2, full: true },
      { name: "body", label: "Body", type: "textarea", rows: 10, full: true },
      { name: "published", label: "Published (visible on site)", type: "checkbox", default: true },
    ],
  },
};
